/**
 * Formatting shared across the Spark Ads screens.
 *
 * Currency comes from the API alongside the figures rather than being assumed, so a
 * brand trading in USD never sees its spend rendered with a pound sign.
 */

const DEFAULT_CURRENCY = 'GBP';

export function formatCurrency(
  value: number | null | undefined,
  currencyCode: string | null | undefined,
  fractionDigits = 2,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currencyCode || DEFAULT_CURRENCY,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(value);
}

/** Compact form for axis ticks and large counts: 4.2M, 412k. */
export function formatShortNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  const abs = Math.abs(value);
  if (abs >= 1000000000) {
    return `${trimZero(value / 1000000000)}B`;
  }
  if (abs >= 1000000) {
    return `${trimZero(value / 1000000)}M`;
  }
  if (abs >= 1000) {
    return `${trimZero(value / 1000)}k`;
  }
  return formatNumber(value);
}

export function formatMultiplier(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  return `${value.toFixed(1)}x`;
}

export function formatPercent(value: number | null | undefined, fractionDigits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  return `${value.toFixed(fractionDigits)}%`;
}

/** Signed percentage for a delta chip, e.g. `+12.4%` / `−8.3%`. Uses a true minus sign. */
export function formatDelta(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  const rounded = Math.abs(value).toFixed(1);
  if (value > 0) {
    return `+${rounded}%`;
  }
  if (value < 0) {
    return `−${rounded}%`;
  }
  return '0.0%';
}

/** Formats a KPI according to the `format` the API declares for it. */
export function formatKpiValue(
  value: number,
  format: 'currency' | 'number' | 'multiplier',
  currencyCode: string | null | undefined,
): string {
  switch (format) {
    case 'currency':
      return formatCurrency(value, currencyCode, value >= 1000 ? 0 : 2);
    case 'multiplier':
      return formatMultiplier(value);
    default:
      return formatShortNumber(value);
  }
}

function trimZero(value: number): string {
  const fixed = value.toFixed(1);
  return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
}
